import { useState, useRef, useCallback } from 'react'
import { ParsedDocument, UploadState } from '../types/document'
import { validateFile, ALLOWED_EXTENSIONS } from '../utils/fileValidation'
import { parseDocument } from '../services/documentParser'

interface UseDocumentUploadOptions {
  onDocumentParsed: (doc: ParsedDocument, filename: string) => void
  onError?: (error: string) => void
}

export function useDocumentUpload({
  onDocumentParsed,
  onError,
}: UseDocumentUploadOptions) {
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    currentFile: null,
    error: null,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    async (file: File) => {
      const validation = validateFile(file)
      if (!validation.valid) {
        onError?.(validation.error!)
        return
      }

      setUploadState({
        isUploading: true,
        progress: 0,
        currentFile: file.name,
        error: null,
      })

      try {
        setUploadState((prev) => ({ ...prev, progress: 50 }))
        const doc = await parseDocument(file)
        setUploadState((prev) => ({ ...prev, progress: 100 }))
        onDocumentParsed(doc, file.name)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to parse document'
        setUploadState((prev) => ({ ...prev, error: message }))
        onError?.(message)
      } finally {
        setUploadState({
          isUploading: false,
          progress: 0,
          currentFile: null,
          error: null,
        })
      }
    },
    [onDocumentParsed, onError]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFile]
  )

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Accept string for file input
  const acceptTypes = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')

  return {
    ...uploadState,
    fileInputRef,
    openFilePicker,
    handleInputChange,
    handleFile,
    acceptTypes,
  }
}
